// Platform config context — Launch Mode + platform-wide toggles.
// Fetched once on app boot from /api/config/public (unauthenticated) and
// refreshable when a Super Admin flips the switch.
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';

const DEFAULT = {
  launch_mode: false,
  show_demo_credentials: true,
  show_sample_data_banner: true,
};

const PlatformConfigContext = createContext({
  config: DEFAULT,
  loading: true,
  refresh: async () => {},
});

export function PlatformConfigProvider({ children }) {
  const [config, setConfig] = useState(DEFAULT);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get('/config/public');
      setConfig({
        launch_mode: !!data.launch_mode,
        show_demo_credentials: !!data.show_demo_credentials,
        show_sample_data_banner: !!data.show_sample_data_banner,
      });
    } catch {
      setConfig(DEFAULT);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <PlatformConfigContext.Provider value={{ config, loading, refresh }}>
      {children}
    </PlatformConfigContext.Provider>
  );
}

export function usePlatformConfig() {
  return useContext(PlatformConfigContext);
}

/** Convenience: true if the platform is running in launch/production mode. */
export function useLaunchMode() {
  return useContext(PlatformConfigContext).config.launch_mode;
}
